import { useMemo } from "react";
import { QRCodeSVG } from "qrcode.react";
import { useLanguage } from "@/hooks/useLanguage";
import { useMounted } from "@/hooks/useMounted";
import { trackEvent } from "@/lib/audit";

/**
 * F3 — QR Quick Access. Renders a scannable QR code that deep-links
 * users (TV viewers, TikTok / YouTube audiences, in-venue physical
 * activations, influencer placements) straight into the welcome flow,
 * carrying campaign / partner / language attribution in the URL.
 */
interface QrEntryBadgeProps {
  /** Path the QR should resolve to, e.g. "/welcome". */
  to?: string;
  /** Free-form attribution source label (broadcast, tiktok, venue…). */
  source?: string;
  /** Optional partner / referrer id. */
  ref?: string;
  /** Optional tenant slug. */
  tenant?: string;
  /** Size in px. */
  size?: number;
  className?: string;
}

export function QrEntryBadge({
  to = "/welcome",
  source = "qr",
  ref,
  tenant,
  size = 96,
  className = "",
}: QrEntryBadgeProps) {
  const { t, lang } = useLanguage();
  // The QR encodes window.location.origin + ?lang=<stored lang>, both of
  // which differ between SSR and the client. Defer rendering until mount
  // so the SSR HTML and the first client render agree (empty placeholder),
  // then the real QR paints in. Avoids React hydration mismatch (#418).
  const mounted = useMounted();

  const url = useMemo(() => {
    const origin =
      typeof window !== "undefined" ? window.location.origin : "https://versesport.com";
    const u = new URL(to, origin);
    u.searchParams.set("src", source);
    u.searchParams.set("lang", lang.toLowerCase());
    if (ref) u.searchParams.set("ref", ref);
    if (tenant) u.searchParams.set("tenant", tenant);
    return u.toString();
  }, [to, source, lang, ref, tenant]);

  return (
    <button
      type="button"
      onClick={() => {
        void trackEvent("qr_entry_badge_revealed", { source, ref, tenant });
        if (typeof navigator !== "undefined" && "clipboard" in navigator) {
          void navigator.clipboard.writeText(url).catch(() => undefined);
        }
      }}
      className={`group inline-flex items-center gap-3 rounded-2xl border border-border/60 bg-background/80 p-3 text-left transition hover:border-primary/50 ${className}`}
    >
      <div className="rounded-lg bg-white p-2">
        {mounted ? (
          <QRCodeSVG
            value={url}
            size={size}
            level="M"
            includeMargin={false}
            bgColor="#ffffff"
            fgColor="#0a0a0a"
          />
        ) : (
          <div style={{ width: size, height: size }} aria-hidden="true" />
        )}
      </div>
      <div className="min-w-0">
        <p className="text-[10px] font-black uppercase tracking-widest text-primary">
          {t("qr_badge_eyebrow")}
        </p>
        <p className="mt-0.5 text-xs font-bold text-foreground">{t("qr_badge_title")}</p>
        <p className="mt-0.5 line-clamp-1 text-[10px] text-muted-foreground">{t("qr_badge_sub")}</p>
      </div>
    </button>
  );
}